import { useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  formatAmountInput,
  formatInteger,
  formatPercentInput,
  parseAmountInput,
  parsePercentInput,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { useFieldIds } from '../../../ds/hooks';
import { InfoTip } from './InfoTip';
import styles from './calculator.module.css';

export type NumberFieldUnit = 'cents' | 'pct' | 'years' | 'months' | 'count';

export interface NumberFieldProps {
  label: string;
  /** Stored value: cents for `cents`, plain numbers otherwise. */
  value: number;
  unit: NumberFieldUnit;
  /** Arrow-key step in the edited unit (euros for `cents`). */
  step: number;
  /** Bounds in the edited unit. */
  min: number;
  max?: number | undefined;
  onChange: (value: number) => void;
  hint?: ReactNode | undefined;
  /** Explanation behind an (i) next to the label (D-347). */
  info?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

const SUFFIX: Record<NumberFieldUnit, string> = {
  cents: '€',
  pct: '%',
  years: 'J.',
  months: 'Mon.',
  count: '×',
};

/** Stored value → the field's text ("145.000,00", "4,4", "10"). */
export function formatFieldValue(unit: NumberFieldUnit, value: number): string {
  if (unit === 'cents') return formatAmountInput(value);
  if (unit === 'pct') return formatPercentInput(value);
  return formatInteger(value);
}

/** The field's text → the stored value (cents for `cents`); null when empty or unreadable. */
export function parseFieldValue(unit: NumberFieldUnit, text: string): number | null {
  if (unit === 'cents') return parseAmountInput(text);
  if (unit === 'pct') return parsePercentInput(text);
  const compact = text.replace(/[\s.\u00a0]/g, '');
  return /^\d+$/.test(compact) ? Number(compact) : null;
}

function toEdit(unit: NumberFieldUnit, stored: number): number {
  return unit === 'cents' ? stored / 100 : stored;
}

function toStored(unit: NumberFieldUnit, edit: number): number {
  if (unit === 'cents') return Math.round(edit * 100);
  if (unit === 'pct') return Math.round(edit * 1000) / 1000;
  return Math.round(edit);
}

/**
 * Typed number input of the calculator: label, value, unit suffix, step via arrow keys, bounds clamped and the
 * text reformatted on blur (format.ts). Valid input is applied while typing so that the results follow.
 */
export function NumberField({
  label,
  value,
  unit,
  step,
  min,
  max,
  onChange,
  hint,
  info,
  disabled = false,
  className,
}: NumberFieldProps) {
  const ids = useFieldIds(undefined, { hint: Boolean(hint), error: false });
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? formatFieldValue(unit, value);

  const clamp = (edit: number) => {
    const lower = Math.max(min, edit);
    return max === undefined ? lower : Math.min(max, lower);
  };

  const commit = (stored: number) => {
    const next = toStored(unit, clamp(toEdit(unit, stored)));
    if (next !== value) onChange(next);
    return next;
  };

  const stepBy = (direction: 1 | -1) => {
    const edit = toEdit(unit, value) + direction * step;
    const rounded = Math.round(edit / step) * step;
    commit(toStored(unit, rounded));
    setDraft(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      stepBy(event.key === 'ArrowUp' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <div className={clsx(styles.numberField, className)}>
      <div className={styles.numberHead}>
        <span className={styles.numberLabelRow}>
          <label htmlFor={ids.inputId} className={styles.numberLabel}>
            {label}
          </label>
          {info ? <InfoTip label={label} text={info} /> : null}
        </span>
        <span className={clsx(styles.numberBox, disabled && styles.numberBoxDisabled)}>
          <input
            id={ids.inputId}
            type="text"
            inputMode={unit === 'cents' || unit === 'pct' ? 'decimal' : 'numeric'}
            autoComplete="off"
            className={styles.numberInput}
            value={text}
            disabled={disabled}
            aria-describedby={ids.describedBy}
            onChange={(event) => {
              setDraft(event.target.value);
              const parsed = parseFieldValue(unit, event.target.value);
              if (parsed !== null) {
                const edit = toEdit(unit, parsed);
                if (edit >= min && (max === undefined || edit <= max) && parsed !== value) onChange(parsed);
              }
            }}
            onBlur={() => {
              if (draft !== null) {
                const parsed = parseFieldValue(unit, draft);
                if (parsed !== null) commit(parsed);
              }
              setDraft(null);
            }}
            onKeyDown={onKeyDown}
          />
          <span className={styles.numberSuffix} aria-hidden="true">
            {SUFFIX[unit]}
          </span>
        </span>
      </div>
      {hint ? (
        <span id={ids.hintId} className={styles.numberHint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
